import React from 'react';
import { Alert } from '@/lib/types';

interface AlertsPanelProps {
  crosswind: number;
  gustSpeed: number;
  metarAgeMinutes: number;
}

function buildAlerts(crosswind: number, gustSpeed: number, metarAge: number): Alert[] {
  const alerts: Alert[] = [];

  if (metarAge > 90) {
    alerts.push({
      id: 'alert-metar-age',
      type: 'caution',
      message: 'METAR ancien (>90 min)',
      detail: `Données vieilles de ${metarAge} min`,
    });
  }

  if (crosswind > 15) {
    alerts.push({
      id: 'alert-crosswind-high',
      type: 'alert',
      message: 'Vent traversier fort',
      detail: `Traversier ${crosswind} kt`,
    });
  } else if (crosswind > 10) {
    alerts.push({
      id: 'alert-crosswind-caution',
      type: 'caution',
      message: 'Vent traversier',
      detail: `Traversier ${crosswind} kt`,
    });
  }

  if (gustSpeed > 25) {
    alerts.push({
      id: 'alert-gusts',
      type: 'alert',
      message: 'Rafales fortes',
      detail: `Rafales ${gustSpeed} kt`,
    });
  }

  return alerts;
}

export default function AlertsPanel({ crosswind, gustSpeed, metarAgeMinutes }: AlertsPanelProps) {
  const alerts = buildAlerts(crosswind, gustSpeed, metarAgeMinutes);

  if (alerts.length === 0) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 bg-green-50 rounded-xl border border-green-200">
        <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
        </div>
        <div>
          <div className="text-base font-black text-green-700">RAS</div>
          <div className="text-xs text-green-600">Aucune alerte importante</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {alerts.map((alert) => (
        <div key={alert.id}
          className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border ${
            alert.type === 'alert' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            alert.type === 'alert' ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            <svg className={`w-4 h-4 ${alert.type === 'alert' ? 'text-red-500' : 'text-amber-500'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <div>
            <div className={`text-xs font-bold ${alert.type === 'alert' ? 'text-red-700' : 'text-amber-700'}`}>
              {alert.message}
            </div>
            {alert.detail && <div className="text-xs text-muted-foreground">{alert.detail}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}